import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Mail, Phone, MapPin, Clock, Globe, MessageCircle, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { getCountryContacts } from '../utils/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    getCountryContacts()
      .then(r => setContacts(r.data || []))
      .catch(() => setContacts([]));
  }, []);

  const grouped = useMemo(() => {
    const m = {};
    contacts.forEach(c => { (m[c.country] = m[c.country] || []).push(c); });
    return m;
  }, [contacts]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Simulate sending message
    setTimeout(() => {
      toast.success('¡Mensaje enviado! Te contactaremos pronto.');
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
      setLoading(false);
    }, 1000);
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      value: 'contacto@iaaspacademy.com',
      link: 'mailto:contacto@iaaspacademy.com'
    },
    {
      icon: Phone,
      title: 'Teléfono',
      value: '+54 11 1234-5678',
      link: 'tel:+541112345678'
    },
    {
      icon: MapPin,
      title: 'Dirección',
      value: 'Buenos Aires, Argentina',
      link: null
    },
    {
      icon: Clock,
      title: 'Horario de Atención',
      value: 'Lun - Vie: 9:00 - 18:00',
      link: null
    }
  ];

  return (
    <div className="py-12 bg-secondary min-h-screen" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-primary mb-4">
            Contactanos
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            ¿Tenés alguna pregunta o consulta? Estamos aquí para ayudarte.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-6">
            {contactInfo.map((info, idx) => (
              <Card key={idx}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{info.title}</h3>
                      {info.link ? (
                        <a 
                          href={info.link} 
                          className="text-sm text-muted-foreground hover:text-accent transition-colors"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-sm text-muted-foreground">{info.value}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Envianos un Mensaje</CardTitle>
                <CardDescription>
                  Completá el formulario y nos pondremos en contacto a la brevedad.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nombre Completo</Label>
                      <Input
                        id="name"
                        placeholder="Juan Pérez"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="tu@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject">Asunto</Label>
                    <Input
                      id="subject"
                      placeholder="¿En qué podemos ayudarte?"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      required
                      data-testid="contact-subject-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Mensaje</Label>
                    <Textarea
                      id="message"
                      placeholder="Escribe tu mensaje aquí..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                      rows={6}
                      data-testid="contact-message-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={loading}
                    data-testid="contact-submit-btn"
                  >
                    {loading ? 'Enviando...' : 'Enviar Mensaje'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Contactos por país */}
        {Object.keys(grouped).length > 0 && (
          <section className="mt-16 pt-10 border-t" data-testid="country-contacts-section">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold mb-3">
                <Globe className="w-3.5 h-3.5" /> PRESENCIA INTERNACIONAL
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-3">Contactos por País</h2>
              <p className="text-base text-muted-foreground max-w-2xl mx-auto">
                Comunícate directamente con el equipo regional más cercano a ti.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([country, list]) => (
                <Card key={country} className="hover:shadow-lg transition-shadow" data-testid={`country-card-${country}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-5 pb-3 border-b">
                      <MapPin className="w-5 h-5 text-accent" />
                      <h3 className="text-xl font-heading font-bold text-primary">{country}</h3>
                    </div>
                    <div className="space-y-5">
                      {list.map(c => {
                        const phone = (c.whatsapp || '').replace(/[^\d+]/g, '').replace(/^\+/, '');
                        const waMsg = encodeURIComponent(`Hola ${c.name}, tengo una consulta sobre IAASP Academy.`);
                        return (
                          <div key={c.id} className="flex gap-3" data-testid={`public-contact-${c.id}`}>
                            {c.avatar_url ? (
                              <img
                                src={c.avatar_url.startsWith('/api') ? `${API_BASE}${c.avatar_url}` : c.avatar_url}
                                alt={c.name}
                                className="w-14 h-14 rounded-full object-cover shrink-0 ring-2 ring-accent/20"
                              />
                            ) : (
                              <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                                <UserIcon className="w-6 h-6 text-accent" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold truncate">{c.name}</p>
                              {c.position && <p className="text-xs text-muted-foreground mb-1.5">{c.position}</p>}
                              <div className="space-y-1 text-xs">
                                {c.email && (
                                  <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 text-muted-foreground hover:text-accent transition-colors truncate">
                                    <Mail className="w-3.5 h-3.5 shrink-0" />
                                    <span className="truncate">{c.email}</span>
                                  </a>
                                )}
                                {c.phone && (
                                  <a href={`tel:${c.phone.replace(/\s/g, '')}`} className="flex items-center gap-1.5 text-muted-foreground hover:text-accent transition-colors">
                                    <Phone className="w-3.5 h-3.5 shrink-0" />
                                    {c.phone}
                                  </a>
                                )}
                                {c.address && (
                                  <p className="flex items-center gap-1.5 text-muted-foreground">
                                    <MapPin className="w-3.5 h-3.5 shrink-0" />
                                    {c.address}
                                  </p>
                                )}
                                {c.notes && (
                                  <p className="flex items-center gap-1.5 text-muted-foreground">
                                    <Clock className="w-3.5 h-3.5 shrink-0" />
                                    {c.notes}
                                  </p>
                                )}
                              </div>
                              {c.whatsapp && (
                                <a
                                  href={`https://wa.me/${phone}?text=${waMsg}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-500 hover:bg-green-600 text-white text-xs font-semibold transition-colors"
                                  data-testid={`contact-wa-${c.id}`}
                                >
                                  <MessageCircle className="w-3.5 h-3.5" />
                                  WhatsApp
                                </a>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default Contact;
